import { IS_AU_SITE, IS_CA_SITE } from "../constants";

type ConsentCategory = "analytics" | "functional" | "advertisement";

export type ConsentChangeDetail = {
	accepted: string[];
	rejected: string[];
};

/**
 * Check if user has given consent for a specific category.
 * - AU/CA users auto-granted (no opt-in requirement)
 * - US/GB users must explicitly consent via CookieYes
 */
export const hasConsent = (category: ConsentCategory): boolean => {
	// AU/CA users auto-granted consent (no opt-in requirement)
	if (IS_AU_SITE || IS_CA_SITE) return true;

	// Check CookieYes cookie
	if (typeof document === "undefined") return false;

	const cookie = document.cookie
		.split("; ")
		.find((row) => row.startsWith("cookieyes-consent="));

	if (!cookie) return false;

	const cookieValue = decodeURIComponent(cookie.split("=")[1] || "");
	return cookieValue.includes(`${category}:yes`);
};

/**
 * Subscribe to consent changes from CookieYes banner.
 * Returns cleanup function to remove listener.
 *
 * Callback receives both accepted and rejected categories,
 * allowing handlers to respond to consent withdrawal mid-session.
 */
export const onConsentChange = (
	callback: (consent: ConsentChangeDetail) => void,
): (() => void) => {
	if (typeof window === "undefined") return () => {};

	const handler = (event: Event) => {
		const detail = (event as CustomEvent<ConsentChangeDetail>).detail;
		if (detail) {
			callback({
				accepted: detail.accepted ?? [],
				rejected: detail.rejected ?? [],
			});
		}
	};

	window.addEventListener("cookieyes_consent_update", handler);
	return () => window.removeEventListener("cookieyes_consent_update", handler);
};
